import { useAuth } from "@/hooks/useAuth";
import { useBookTags } from "@/hooks/useBookTags";
import { BookTagInput } from "./BookTagInput";
import { PopularBookTags } from "./PopularBookTags";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tag } from "lucide-react";

interface BookTagsSectionProps {
  bookId: string;
}

export function BookTagsSection({ bookId }: BookTagsSectionProps) {
  const { user } = useAuth();
  const {
    popularTags,
    userTags,
    isLoading,
    addTag,
    removeTag,
    isAddingTag,
    isRemovingTag,
  } = useBookTags(bookId, user?.id);

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 w-32 bg-muted rounded" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Tag className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Tags</h2>
      </div>

      {/* Popular tags */}
      {popularTags.length > 0 && (
        <>
          <PopularBookTags
            tags={popularTags}
            userTags={userTags}
            onAddTag={addTag}
            disabled={!user || isAddingTag}
          />
          <Separator />
        </>
      )}

      {/* User's tag input */}
      {user ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Your Tags</h3>
          <BookTagInput
            bookId={bookId}
            userId={user.id}
            selectedTags={userTags}
            onAddTag={addTag}
            onRemoveTag={removeTag}
            disabled={isAddingTag || isRemovingTag}
          />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Sign in to add your own tags
        </p>
      )}
    </Card>
  );
}
